'use client';

import { useEffect, useState } from 'react';
import { format, isAfter, isValid, parse, parseISO } from 'date-fns';

import {
  Alert,
  Button,
  Close,
  DateInput,
  Heading,
  Input,
  MMIDInfo,
  Modal,
  useAuth,
} from '@/components';
import { formatDate, mapUpdateAccountInput } from '@/components/Account';
import {
  useEcomStoreSelector,
  useForm,
  useGetAccount,
  useUpdateAccount,
  useValidator,
} from '@/data';
import { EnumCustomerType as CustomerType } from '@/types';
import { CookiesNames } from '@/constants';
import { useCookies, useFeaturedFlag } from '@/hooks';

import { omit } from 'lodash';

import styles from './MedRecValidator.module.scss';

const MedRecValidator = () => {
  const { setCookieValue } = useCookies();
  const {
    customer_type,
    next_customer_type,
    medical_id,
    medical_id_expiration_date,
    setState,
  } = useEcomStoreSelector([
    'customer_type',
    'next_customer_type',
    'medical_id',
    'medical_id_expiration_date',
  ]);
  const [showAlert, setShowAlert] = useState(true);

  const { inputs, setInputs, inputChange, dateChange } = useForm();

  const { enableMMIDTooltip } = useFeaturedFlag();

  const { isAuth, email } = useAuth();
  const { updateAccount } = useUpdateAccount();
  const [initialMedicalId, setInitialMedicalId] = useState('');
  const [initialMedicalIdExpirationDate, setInitialMedicalIdExpirationDate] =
    useState('');

  const { validator, isInvalid, setIsSubmited } = useValidator(
    [
      {
        field: 'medical_id_expiration_date',
        message: 'Invalid date',
        method: current => {
          const parsedDate = new Date(current);
          const isValidDate = isValid(parsedDate);
          const today = new Date();
          return isValidDate ? isAfter(parsedDate, today) : false;
        },
        validWhen: true,
      },
      {
        args: [{ min: 5 }],
        field: 'medical_id',
        message: 'Write at least 5 characters.',
        method: 'isLength',
        validWhen: true,
      },
    ],
    inputs,
  );

  const { loading: isGettingAccount } = useGetAccount({
    onCompleted: res => {
      const fetchedMedicalId = res.getAccountByEmail?.medical_id?.number ?? '';
      const fetchedExpirationDate = formatDate(
        res.getAccountByEmail?.medical_id?.expiration_date,
      );

      setInputs({
        ...(omit(res.getAccountByEmail, [
          'medical_id',
          'medical_document_photo_uri',
        ]) ?? {}),
        medical_id: fetchedMedicalId,
        medical_id_expiration_date: fetchedExpirationDate,
      });
      setInitialMedicalId(fetchedMedicalId);
      setInitialMedicalIdExpirationDate(fetchedExpirationDate);
    },
    fetchPolicy: 'network-only',
    skip: !isAuth || !email,
    variables: {
      email,
    },
  });
  useEffect(() => {
    if (isAuth) return;

    let formatedExpDate = '';
    if (medical_id_expiration_date) {
      formatedExpDate = format(
        parseISO(medical_id_expiration_date),
        'MM-dd-yyyy',
      );
    }
    setInputs({
      medical_id,
      medical_id_expiration_date: formatedExpDate,
    });
  }, [isAuth, medical_id, medical_id_expiration_date, setInputs]);

  const isModalVisible = ![customer_type, null].includes(next_customer_type);

  const confirmCustomerTypeChange = async () => {
    setIsSubmited(true);

    if (isInvalid && next_customer_type === CustomerType.MEDICAL) {
      return;
    }

    const {
      medical_id,
      medical_id_expiration_date: input_medical_id_expiration_date,
    } = inputs;

    let formatedExpDate = '';
    if (input_medical_id_expiration_date) {
      const parsedDate = parse(
        input_medical_id_expiration_date,
        'MM/dd/yyyy',
        new Date(),
      );
      if (isValid(parsedDate)) {
        formatedExpDate = format(parsedDate, 'yyyy-MM-dd');
      }
    }

    if (isAuth) {
      const nextMedicalId = (medical_id ?? '').trim();
      const nextExpDate = formatedExpDate;
      const currentMedicalId = initialMedicalId.trim();
      const currentExpDate = initialMedicalIdExpirationDate
        ? format(
            parse(initialMedicalIdExpirationDate, 'MM/dd/yyyy', new Date()),
            'yyyy-MM-dd',
          )
        : '';

      const hasChanges =
        nextMedicalId !== currentMedicalId || nextExpDate !== currentExpDate;

      if (hasChanges) {
        try {
          await updateAccount({
            variables: {
              input: mapUpdateAccountInput({
                ...inputs,
                medical_id: nextMedicalId,
                medical_id_expiration_date: input_medical_id_expiration_date,
              }),
            },
          });
          setInitialMedicalId(nextMedicalId);
          setInitialMedicalIdExpirationDate(
            nextExpDate
              ? format(
                  parse(nextExpDate, 'yyyy-MM-dd', new Date()),
                  'MM/dd/yyyy',
                )
              : '',
          );
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to update medical info:', error);
        }
      }
    }

    const parsedExpirationDate = input_medical_id_expiration_date
      ? parse(input_medical_id_expiration_date, 'MM/dd/yyyy', new Date())
      : null;
    const expirationDateIso =
      parsedExpirationDate && isValid(parsedExpirationDate)
        ? parsedExpirationDate.toISOString()
        : '';

    setState({
      line_items: [],
      customer_type: next_customer_type!,
      next_customer_type: null,
      medical_id: medical_id,
      medical_id_expiration_date: expirationDateIso,
    });

    setCookieValue(CookiesNames._CUSTOMER_TYPE, next_customer_type!);
  };

  const confirmKeepCustomerType = () => {
    setState({
      next_customer_type: null,
    });
  };

  const modalTitleId = 'modal-title';
  const modalDescriptionId = 'modal-description';

  return (
    <Modal
      aria-describedby={modalDescriptionId}
      aria-labelledby={modalTitleId}
      className={styles.modal}
      isOpen={isModalVisible}
      md
    >
      <div className={styles.store_validator}>
        <div className={styles.store_validator__header}>
          <Heading className={styles.header_title} id={modalTitleId} level={2}>
            Are you sure you want to switch modes?
          </Heading>
          <Close
            aria-label="Close modal dialog"
            onClick={confirmKeepCustomerType}
          />
        </div>
        <div id={modalDescriptionId}>
          <Alert
            className={styles.warning_message}
            isOpen={showAlert}
            toggle={() => {
              setShowAlert(false);
            }}
            warning={true}
          >
            <p>
              Be careful! You are about to change shopping mode. Your items will
              be removed from your shopping cart.
            </p>
          </Alert>
        </div>
        {next_customer_type === CustomerType.MEDICAL && (
          <div className={styles.med_form__row}>
            {enableMMIDTooltip && (
              <p className={styles.med_form__row__tooltip}>
                California MMID Patients only. Provide your ID & Exp. date. If
                medical regular customer, please click to proceed.
              </p>
            )}
            <Input
              feedback={validator.medical_id.message}
              inputLabel={enableMMIDTooltip ? 'MMID Only' : 'Medical ID'}
              isInvalid={validator.medical_id.isInvalid}
              name="medical_id"
              onChange={inputChange}
              placeholder={
                enableMMIDTooltip ? 'MMID Only (Optional)' : 'Medical ID'
              }
              rightAdornment={
                enableMMIDTooltip ? (
                  <MMIDInfo anchorPlacement="bottom" />
                ) : undefined
              }
              value={inputs.medical_id ?? ''}
            />
            <DateInput
              autoComplete="off"
              feedback={validator.medical_id_expiration_date.message}
              inputLabel="Medical ID expiration date"
              isInvalid={validator.medical_id_expiration_date.isInvalid}
              name="medical_id_expiration_date"
              onChange={dateChange}
              placeholder="Medical ID expiration date"
              type="date"
              value={inputs.medical_id_expiration_date ?? ''}
            />
          </div>
        )}
        <div className={styles.store_validator__footer}>
          <Button
            color="primary"
            loading={isGettingAccount}
            onClick={confirmCustomerTypeChange}
          >
            Remove items & change mode
          </Button>
          <Button
            color="secondary"
            loading={isGettingAccount}
            onClick={confirmKeepCustomerType}
          >
            Keep items & don’t change mode
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export { MedRecValidator };
